"use strict";

const SCHEMA_VERSION = 1;
const STEP_TYPES = ["content", "quiz", "scenario", "ordering", "hotspot", "shortAnswer", "timeline", "checklist", "caseStudy"];
const BLOCK_TYPES = ["text", "callout", "list", "image", "video", "disclosure"];
const DIFFICULTIES = ["foundation", "intermediate", "advanced"];
const STAFF_TRAINING_TYPES = ["Design", "Certification", "Project Management", "Design certification", "Other"];
const QUESTION_KINDS = ["single", "multi", "boolean"];

const stringItem = { type: "string" };
const optionSchema = {
	type: "object",
	additionalProperties: false,
	required: ["id", "label", "consequence", "nextStepId"],
	properties: {
		id: { type: "string" },
		label: { type: "string" },
		consequence: { type: "string" },
		nextStepId: { type: "string" },
	},
};
const questionSchema = {
	type: "object",
	additionalProperties: false,
	required: ["id", "prompt", "kind", "options", "correctOptionIds", "explanation"],
	properties: {
		id: { type: "string" },
		prompt: { type: "string" },
		kind: { type: "string", enum: QUESTION_KINDS },
		options: { type: "array", items: optionSchema },
		correctOptionIds: { type: "array", items: stringItem },
		explanation: { type: "string" },
	},
};
const hotspotSchema = {
	type: "object",
	additionalProperties: false,
	required: ["id", "label", "detail", "x", "y"],
	properties: {
		id: { type: "string" },
		label: { type: "string" },
		detail: { type: "string" },
		x: { type: "number" },
		y: { type: "number" },
	},
};
const stageSchema = {
	type: "object",
	additionalProperties: false,
	required: ["id", "label", "detail"],
	properties: {
		id: { type: "string" },
		label: { type: "string" },
		detail: { type: "string" },
	},
};
const checkSchema = {
	type: "object",
	additionalProperties: false,
	required: ["id", "label", "required"],
	properties: {
		id: { type: "string" },
		label: { type: "string" },
		required: { type: "boolean" },
	},
};
const stepSchema = {
	type: "object",
	additionalProperties: false,
	required: [
		"id", "title", "type", "required", "body", "callout", "items", "questions",
		"options", "correctOptionIds", "explanation", "keywords", "hotspots",
		"stages", "checks", "nextStepId", "imageUrl", "videoUrl",
	],
	properties: {
		id: { type: "string" },
		title: { type: "string" },
		type: { type: "string", enum: STEP_TYPES },
		required: { type: "boolean" },
		body: { type: "string" },
		callout: { type: "string" },
		items: { type: "array", items: stringItem },
		questions: { type: "array", items: questionSchema },
		options: { type: "array", items: optionSchema },
		correctOptionIds: { type: "array", items: stringItem },
		explanation: { type: "string" },
		keywords: { type: "array", items: stringItem },
		hotspots: { type: "array", items: hotspotSchema },
		stages: { type: "array", items: stageSchema },
		checks: { type: "array", items: checkSchema },
		nextStepId: { type: "string" },
		imageUrl: { type: "string" },
		videoUrl: { type: "string" },
	},
};

const GENERATION_JSON_SCHEMA = {
	type: "object",
	additionalProperties: false,
	required: [
		"title", "description", "estimatedMinutes", "difficulty", "audience",
		"objectives", "staffTrainingType", "passThreshold", "freeNavigation", "steps",
	],
	properties: {
		title: { type: "string" },
		description: { type: "string" },
		estimatedMinutes: { type: "integer" },
		difficulty: { type: "string", enum: DIFFICULTIES },
		audience: { type: "string" },
		objectives: { type: "array", items: stringItem },
		staffTrainingType: { type: "string", enum: STAFF_TRAINING_TYPES },
		passThreshold: { type: "integer" },
		freeNavigation: { type: "boolean" },
		steps: { type: "array", items: stepSchema },
	},
};

const CONFLICT_JSON_SCHEMA = {
	type: "object",
	additionalProperties: false,
	required: ["conflicts"],
	properties: {
		conflicts: {
			type: "array",
			items: {
				type: "object",
				additionalProperties: false,
				required: ["stepId", "handbookId", "handbookTitle", "revision", "issue"],
				properties: {
					stepId: { type: "string" },
					handbookId: { type: "string" },
					handbookTitle: { type: "string" },
					revision: { type: "string" },
					issue: { type: "string" },
				},
			},
		},
	},
};

function asList(value) {
	if (Array.isArray(value)) return value.filter((item) => item != null);
	if (!value || typeof value !== "object") return [];
	return Object.keys(value)
		.sort((a, b) => Number(a) - Number(b))
		.map((key) => value[key])
		.filter((item) => item != null);
}

function text(value, fallback = "") {
	if (typeof value !== "string") return fallback;
	const trimmed = value.trim();
	return trimmed || fallback;
}

function clamp(value, min, max, fallback) {
	const number = Number(value);
	if (!Number.isFinite(number)) return fallback;
	return Math.min(max, Math.max(min, Math.round(number)));
}

function safeUrl(value) {
	const trimmed = text(value);
	if (!/^https?:\/\//i.test(trimmed)) return "";
	return trimmed;
}

function slug(value, fallback) {
	const cleaned = text(value).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
	return cleaned.slice(0, 40) || fallback;
}

function uniqueId(value, fallback, used) {
	let id = slug(value, fallback);
	let suffix = 2;
	while (used.has(id)) {
		id = `${slug(value, fallback)}-${suffix}`;
		suffix += 1;
	}
	used.add(id);
	return id;
}

function stringList(value, limit = 12) {
	return asList(value).map((item) => text(item)).filter(Boolean).slice(0, limit);
}

function handbookCollectionName(instance) {
	if (typeof instance !== "string" || !instance.trim()) return "";
	return `${instance.trim()}-handbooks`;
}

function currentHandbooks(records) {
	const list = [];
	for (const [key, record] of Object.entries(records || {})) {
		if (!record || typeof record !== "object") continue;
		const id = text(record.ID, key);
		const revisionId = text(record.RevisionID);
		const revision = revisionId ? record.Revisions?.[revisionId] : null;
		if (!revisionId || !revision) continue;
		list.push({
			id,
			name: text(record.Name, id),
			revisionId,
			revision: revision.Revision == null ? "" : String(revision.Revision),
		});
	}
	return list;
}

function filterProcedureHits(matches, handbooks) {
	const byId = new Map((handbooks || []).map((handbook) => [handbook.id, handbook]));
	const kept = [];
	for (const match of matches || []) {
		const recordId = match?.fields?.recordId;
		const handbook = byId.get(recordId);
		if (!handbook) continue;
		const revision = match?.fields?.revision == null ? "" : String(match.fields.revision);
		if (!revision || revision !== handbook.revision) continue;
		kept.push(match);
	}
	return kept;
}

function normalizeQuestions(questions, used) {
	return asList(questions).slice(0, 8).map((question, index) => {
		const kind = QUESTION_KINDS.includes(question?.kind) ? question.kind : "single";
		let options = asList(question?.options).slice(0, 6).map((option, optionIndex) => ({
			id: uniqueId(option?.id || option?.label, `option-${optionIndex + 1}`, used),
			label: text(option?.label, `Option ${optionIndex + 1}`),
		}));
		if (kind === "boolean" && options.length < 2) {
			options = [
				{ id: uniqueId("true", "true", used), label: "True" },
				{ id: uniqueId("false", "false", used), label: "False" },
			];
		}
		const optionIds = new Set(options.map((option) => option.id));
		const correctOptionIds = stringList(question?.correctOptionIds, 6).filter((id) => optionIds.has(id));
		return {
			id: uniqueId(question?.id || question?.prompt, `question-${index + 1}`, used),
			prompt: text(question?.prompt, "Question"),
			kind,
			options,
			correctOptionIds,
			explanation: text(question?.explanation),
		};
	}).filter((question) => question.prompt && question.options.length > 0);
}

function normalizeStep(raw, index, stepIds) {
	const used = new Set(stepIds);
	const type = STEP_TYPES.includes(raw?.type) ? raw.type : "content";
	const id = uniqueId(raw?.id || raw?.title, `step-${index + 1}`, stepIds);
	const blocks = [];
	const body = text(raw?.body);
	const callout = text(raw?.callout);
	if (body) blocks.push({ type: "text", text: body });
	if (callout) blocks.push({ type: "callout", tone: "note", text: callout });
	const imageUrl = safeUrl(raw?.imageUrl);
	const videoUrl = safeUrl(raw?.videoUrl);
	if (imageUrl) blocks.push({ type: "image", url: imageUrl, alt: text(raw?.title, "Diagram") });
	if (videoUrl) blocks.push({ type: "video", url: videoUrl });
	if (type === "content" || type === "caseStudy") {
		const items = stringList(raw?.items, 12);
		if (items.length) blocks.push({ type: "list", items });
	}

	let activity = null;
	if (type === "quiz" || type === "caseStudy") {
		activity = { questions: normalizeQuestions(raw?.questions, used) };
	} else if (type === "scenario") {
		const options = asList(raw?.options).slice(0, 6).map((option, optionIndex) => ({
			id: uniqueId(option?.id || option?.label, `choice-${optionIndex + 1}`, used),
			label: text(option?.label, `Choice ${optionIndex + 1}`),
			consequence: text(option?.consequence),
			nextStepId: slug(option?.nextStepId, ""),
		}));
		const optionIds = new Set(options.map((option) => option.id));
		activity = {
			prompt: body,
			options,
			correctOptionIds: stringList(raw?.correctOptionIds, 6).filter((optionId) => optionIds.has(optionId)),
			explanation: text(raw?.explanation),
		};
	} else if (type === "ordering") {
		const items = stringList(raw?.items, 8).map((label, itemIndex) => ({
			id: uniqueId(label, `item-${itemIndex + 1}`, used),
			label,
		}));
		activity = {
			prompt: body || "Put these items in the correct order.",
			items,
			correctOrder: items.map((item) => item.id),
		};
	} else if (type === "hotspot") {
		activity = {
			imageUrl,
			alt: text(raw?.title, "Diagram"),
			hotspots: asList(raw?.hotspots).slice(0, 8).map((hotspot, hotspotIndex) => ({
				id: uniqueId(hotspot?.id || hotspot?.label, `hotspot-${hotspotIndex + 1}`, used),
				label: text(hotspot?.label, `Detail ${hotspotIndex + 1}`),
				detail: text(hotspot?.detail),
				x: clamp(hotspot?.x, 0, 100, 50),
				y: clamp(hotspot?.y, 0, 100, 50),
			})),
		};
	} else if (type === "shortAnswer") {
		activity = {
			prompt: body || text(raw?.title, "Short answer"),
			keywords: stringList(raw?.keywords, 8),
			explanation: text(raw?.explanation),
		};
	} else if (type === "timeline") {
		activity = {
			stages: asList(raw?.stages).slice(0, 8).map((stage, stageIndex) => ({
				id: uniqueId(stage?.id || stage?.label, `stage-${stageIndex + 1}`, used),
				label: text(stage?.label, `Stage ${stageIndex + 1}`),
				detail: text(stage?.detail),
			})),
		};
	} else if (type === "checklist") {
		const checks = asList(raw?.checks);
		const source = checks.length ? checks : stringList(raw?.items, 12).map((label) => ({ label, required: true }));
		activity = {
			items: source.slice(0, 12).map((item, itemIndex) => ({
				id: uniqueId(item?.id || item?.label, `check-${itemIndex + 1}`, used),
				label: text(item?.label || item, `Item ${itemIndex + 1}`),
				required: item?.required !== false,
			})),
		};
	}

	const next = [];
	const nextStepId = slug(raw?.nextStepId, "");
	if (nextStepId) next.push({ when: "default", stepId: nextStepId });
	for (const option of activity?.options || []) {
		if (option.nextStepId) next.push({ when: `choice:${option.id}`, stepId: option.nextStepId });
	}

	return {
		id,
		title: text(raw?.title, `Step ${index + 1}`),
		type,
		required: raw?.required !== false,
		next,
		blocks,
		activity,
	};
}

function normalizeCourse(raw) {
	const stepIds = new Set();
	const steps = asList(raw?.steps).slice(0, 24).map((step, index) => normalizeStep(step, index, stepIds));
	const known = new Set(steps.map((step) => step.id));
	for (const step of steps) {
		step.next = step.next.filter((rule) => known.has(rule.stepId) && rule.stepId !== step.id);
		for (const option of step.activity?.options || []) {
			if (option.nextStepId && !known.has(option.nextStepId)) option.nextStepId = "";
		}
	}
	const threshold = clamp(raw?.passThreshold, 0, 100, 80);
	const passThreshold = threshold > 0 ? threshold : null;
	return {
		schemaVersion: SCHEMA_VERSION,
		meta: {
			title: text(raw?.title, "Untitled course"),
			description: text(raw?.description),
			estimatedMinutes: clamp(raw?.estimatedMinutes, 1, 600, 15),
			difficulty: DIFFICULTIES.includes(raw?.difficulty) ? raw.difficulty : "foundation",
			audience: text(raw?.audience),
			objectives: stringList(raw?.objectives, 8),
			staffTrainingType: STAFF_TRAINING_TYPES.includes(raw?.staffTrainingType) ? raw.staffTrainingType : "Other",
			passThreshold,
			freeNavigation: raw?.freeNavigation === true,
			materialId: text(raw?.materialId) || null,
		},
		steps,
		completion: {
			requireAllRequiredSteps: true,
			passThreshold,
		},
	};
}

function restoreCourse(schema) {
	if (!schema || typeof schema !== "object") return null;
	const steps = asList(schema.steps).map((step) => ({
		...step,
		next: asList(step?.next),
		blocks: asList(step?.blocks).map((block) => ({
			...block,
			items: asList(block?.items),
		})),
		activity: step?.activity ? {
			...step.activity,
			questions: asList(step.activity.questions).map((question) => ({
				...question,
				options: asList(question?.options),
				correctOptionIds: asList(question?.correctOptionIds),
			})),
			options: asList(step.activity.options),
			correctOptionIds: asList(step.activity.correctOptionIds),
			items: asList(step.activity.items),
			correctOrder: asList(step.activity.correctOrder),
			keywords: asList(step.activity.keywords),
			hotspots: asList(step.activity.hotspots),
			stages: asList(step.activity.stages),
		} : null,
	}));
	return {
		...schema,
		meta: { ...(schema.meta || {}) },
		steps,
		completion: { ...(schema.completion || {}) },
	};
}

function validateCourseSchema(input) {
	const schema = restoreCourse(input);
	const errors = [];
	if (!schema || schema.schemaVersion !== SCHEMA_VERSION) errors.push("Course schema version is not supported.");
	if (!text(schema?.meta?.title)) errors.push("Course title is required.");
	if (!Array.isArray(schema?.steps) || schema.steps.length === 0) errors.push("Course needs at least one step.");
	const ids = new Set();
	for (const step of schema?.steps || []) {
		if (!text(step?.id)) errors.push("A step is missing an id.");
		else if (ids.has(step.id)) errors.push(`Step id ${step.id} is duplicated.`);
		else ids.add(step.id);
		if (!STEP_TYPES.includes(step?.type)) errors.push(`Step ${step?.id || "?"} has an unknown type.`);
		for (const block of step?.blocks || []) {
			if (!BLOCK_TYPES.includes(block?.type)) errors.push(`Step ${step.id} has an unknown content block.`);
		}
		if (step?.type === "quiz" || step?.type === "caseStudy") {
			const questions = step.activity?.questions || [];
			if (!questions.length) errors.push(`Step ${step.id} needs at least one question.`);
			for (const question of questions) {
				const optionIds = new Set((question.options || []).map((option) => option.id));
				if (!optionIds.size) errors.push(`Question ${question.id || "?"} has no options.`);
				if (!(question.correctOptionIds || []).length) errors.push(`Question ${question.id || "?"} needs a correct answer.`);
				for (const correctId of question.correctOptionIds || []) {
					if (!optionIds.has(correctId)) errors.push(`Question ${question.id} has a correct option that does not exist.`);
				}
			}
		}
	}
	for (const step of schema?.steps || []) {
		for (const rule of step?.next || []) {
			if (!ids.has(rule?.stepId)) errors.push(`Step ${step.id} branches to missing step ${rule?.stepId}.`);
		}
	}
	const threshold = schema?.completion?.passThreshold;
	if (threshold != null && (typeof threshold !== "number" || threshold < 1 || threshold > 100)) {
		errors.push("Pass threshold must be between 1 and 100.");
	}
	return { ok: errors.length === 0, errors, schema };
}

function sameIds(left, right) {
	if (left.length !== right.length) return false;
	return left.every((id, index) => id === right[index]);
}

function scoreAnswer(step, answer) {
	if (!step) return { scored: false, score: null, complete: false };
	if (step.type === "quiz" || step.type === "caseStudy") {
		const questions = step.activity?.questions || [];
		if (!questions.length) return { scored: false, score: null, complete: false };
		const responses = answer?.questions || {};
		const complete = questions.every((question) => asList(responses[question.id]).length > 0);
		if (!complete) return { scored: true, score: null, complete: false };
		let correct = 0;
		for (const question of questions) {
			const selected = asList(responses[question.id]).slice().sort();
			const expected = asList(question.correctOptionIds).slice().sort();
			if (sameIds(selected, expected)) correct += 1;
		}
		return { scored: true, score: Math.round((correct / questions.length) * 100), complete: true };
	}
	if (step.type === "shortAnswer") {
		const value = text(answer?.text).toLowerCase();
		if (!value) return { scored: true, score: null, complete: false };
		const keywords = asList(step.activity?.keywords).map((keyword) => keyword.toLowerCase()).filter(Boolean);
		const matched = keywords.length ? keywords.some((keyword) => value.includes(keyword)) : true;
		return { scored: true, score: matched ? 100 : 0, complete: true };
	}
	if (step.type === "ordering") {
		const expected = asList(step.activity?.correctOrder);
		const given = asList(answer?.order);
		if (given.length !== expected.length) return { scored: true, score: null, complete: false };
		return { scored: true, score: sameIds(given, expected) ? 100 : 0, complete: true };
	}
	if (step.type === "scenario") {
		const optionId = text(answer?.optionId);
		if (!optionId) return { scored: false, score: null, complete: false };
		const correct = asList(step.activity?.correctOptionIds);
		if (!correct.length) return { scored: false, score: null, complete: true };
		return { scored: true, score: correct.includes(optionId) ? 100 : 0, complete: true };
	}
	if (step.type === "checklist") {
		const items = asList(step.activity?.items);
		const required = items.filter((item) => item.required !== false);
		const checked = answer?.checked || {};
		const complete = required.length
			? required.every((item) => checked[item.id] === true)
			: items.every((item) => checked[item.id] === true);
		return { scored: false, score: null, complete };
	}
	return { scored: false, score: null, complete: true };
}

function nextStepId(schema, step, result) {
	const rules = asList(step?.next);
	const wanted = [];
	const choice = text(result?.answer?.optionId);
	if (choice) wanted.push(`choice:${choice}`);
	if (result?.scored && typeof result.score === "number") {
		const threshold = schema?.completion?.passThreshold ?? 100;
		wanted.push(result.score >= threshold ? "pass" : "fail");
	}
	wanted.push("default");
	for (const when of wanted) {
		const rule = rules.find((item) => item.when === when && item.stepId);
		if (rule) return rule.stepId;
	}
	const index = (schema?.steps || []).findIndex((item) => item.id === step?.id);
	return schema?.steps?.[index + 1]?.id || null;
}

function courseResult(schema, progress) {
	const steps = schema?.steps || [];
	const required = steps.filter((step) => step.required !== false);
	const completed = progress?.Completed || {};
	const requiredDone = required.every((step) => completed[step.id] === true);
	const scores = steps
		.map((step) => progress?.StepScores?.[step.id])
		.filter((score) => typeof score === "number");
	const score = scores.length ? Math.round(scores.reduce((sum, value) => sum + value, 0) / scores.length) : null;
	const threshold = schema?.completion?.passThreshold;
	const passed = requiredDone && (threshold == null || score == null || score >= threshold);
	return { score, passed, requiredDone };
}

function dateOnly(value) {
	const date = value instanceof Date ? value : new Date(value);
	const valid = Number.isNaN(date.getTime()) ? new Date() : date;
	const month = String(valid.getMonth() + 1).padStart(2, "0");
	const day = String(valid.getDate()).padStart(2, "0");
	return `${valid.getFullYear()}-${month}-${day}`;
}

function buildStaffTrainingRecord({ course, score, passed, startedAt, userId, now = new Date() }) {
	const title = text(course?.meta?.title || course?.Title, "Course");
	const type = STAFF_TRAINING_TYPES.includes(course?.meta?.staffTrainingType) ? course.meta.staffTrainingType : "Other";
	const numericScore = typeof score === "number" && Number.isFinite(score) ? Math.round(score) : null;
	const courseId = text(course?.id || course?.ID);
	const result = passed ? "Passed" : "Did not pass";
	const scoreText = numericScore == null ? "n/a" : `${numericScore}%`;
	return {
		Date: {
			Start: dateOnly(startedAt || now),
			Complete: dateOnly(now),
		},
		Type: type,
		ApprovedBy: "Course completion",
		Training: title,
		Outcomes: `Completed interactive course ${title}. Score: ${scoreText}. ${result}.${courseId ? ` Course ID: ${courseId}.` : ""}`,
		AddedBy: userId || "",
		CourseID: courseId,
		Score: numericScore,
	};
}

function acceptedConflicts(conflicts, handbooks) {
	const byId = new Map((handbooks || []).map((handbook) => [handbook.id, handbook]));
	const kept = [];
	for (const conflict of asList(conflicts)) {
		const handbookId = text(conflict?.handbookId || conflict?.HandbookID);
		const handbook = byId.get(handbookId);
		if (!handbook) continue;
		const revision = text(conflict?.revision || conflict?.Revision, handbook.revision);
		if (revision !== handbook.revision) continue;
		const issue = text(conflict?.issue || conflict?.Issue);
		if (!issue) continue;
		kept.push({
			StepID: text(conflict?.stepId || conflict?.StepID),
			HandbookID: handbook.id,
			HandbookTitle: text(conflict?.handbookTitle || conflict?.HandbookTitle, handbook.name),
			Revision: handbook.revision,
			Issue: issue,
		});
	}
	return kept.slice(0, 12);
}

export {
	BLOCK_TYPES,
	CONFLICT_JSON_SCHEMA,
	DIFFICULTIES,
	GENERATION_JSON_SCHEMA,
	SCHEMA_VERSION,
	STAFF_TRAINING_TYPES,
	STEP_TYPES,
	acceptedConflicts,
	buildStaffTrainingRecord,
	courseResult,
	currentHandbooks,
	dateOnly,
	filterProcedureHits,
	handbookCollectionName,
	nextStepId,
	normalizeCourse,
	restoreCourse,
	scoreAnswer,
	validateCourseSchema,
};
